import {
  WorkspaceResolutionService,
  type WorkspaceResolutionResponseDto,
} from '@inference.engine/persona-api-client/web';
import { queryOptions } from '@tanstack/react-query';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { personasHttp } from '@/lib/api/personas-client';

/**
 * Resolve a focus group id to its owning workspace/organization. Mirrors
 * `entities/analysis`'s `resolveAnalysis`: the resolution seam maps a 404 to
 * its own generic "not available" state, so the global toast is suppressed
 * here — it would otherwise leak the raw error first.
 */
export const resolveFocusGroup = async (id: string): Promise<WorkspaceResolutionResponseDto> => {
  const response = await WorkspaceResolutionService.resolveFocusGroupWorkspace({
    client: personasHttp,
    path: { id },
    // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to resolve focus group');
  }

  return response.data;
};

/**
 * Deep-link resolution of a focus group id to its owning workspace. Runs BEFORE
 * the workspace-scoped focus-group fetch (via `useDeepLinkResolution`) so a deep
 * link from another workspace doesn't 404 under the ambient
 * `x-selected-workspace-id` header. The key extends the family's detail tuple
 * (`['focusGroup', id]`) so a detail invalidation also re-resolves.
 */
export const focusGroupResolutionQuery = (id?: string) =>
  queryOptions({
    queryKey: ['focusGroup', id ?? '', 'resolution'] as const,
    queryFn: () => resolveFocusGroup(id!),
  });
